import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUploadCloud, FiEye, FiEdit3, FiPackage } = FiIcons;

const Process = () => {
  const steps = [
    { title: "Ingestion", icon: FiUploadCloud, desc: "Upload raw files to our secure cloud infrastructure." },
    { title: "Curation", icon: FiEye, desc: "We extract the narrative hooks and golden moments." },
    { title: "Refinement", icon: FiEdit3, desc: "Two-stage editorial pass for absolute perfection." },
    { title: "Delivery", icon: FiPackage, desc: "Publish-ready content delivered on a fixed schedule." }
  ];

  // Optimized image with lazy loading and explicit size
  const processImageUrl = "https://res.cloudinary.com/digw3ykex/image/upload/f_auto,q_auto,w_800/v1767262708/STOCK_EDITING_wptx97.png";

  return (
    <section id="process" className="bg-black py-16 md:py-32 section-curve-up">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 md:gap-20 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-7xl font-black text-white tracking-tighter leading-none mb-10 md:mb-12 uppercase">
              THE <span className="text-[#BEF264]">PIPELINE</span>
            </h2>
            
            <div className="space-y-0">
              {steps.map((step, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  className="group relative py-5 md:py-8 border-b border-white/10 overflow-hidden"
                >
                  <div className="flex flex-col md:flex-row md:justify-between md:items-center relative z-10 gap-3 md:gap-4">
                    <div className="flex items-center gap-5 md:gap-6 transition-transform duration-500 md:group-hover:translate-x-4">
                      <div className="flex flex-col">
                        <span className="text-[#BEF264] text-[8px] md:text-[9px] font-black tracking-[0.4em] uppercase mb-1 block">Stage 0{index + 1}</span>
                        <h3 className="text-lg md:text-2xl font-black text-white uppercase">{step.title}</h3>
                      </div>
                      <SafeIcon icon={step.icon} className="w-4 h-4 md:w-5 md:h-5 text-white/20 group-hover:text-[#BEF264] transition-colors stroke-[1.5px]" />
                    </div>
                    <p className="text-white/60 md:max-w-[500px] text-[13px] md:text-[15px] leading-relaxed tracking-tight font-medium md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-500 md:pr-8 md:text-right md:whitespace-nowrap">
                      {step.desc}
                    </p>
                  </div>
                  <div className="absolute top-0 left-0 w-full h-full bg-[#BEF264]/5 -translate-x-full group-hover:translate-x-0 transition-transform duration-500 hidden md:block" />
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative hidden md:block"
          >
            <div className="absolute -top-10 -right-10 w-40 h-40 border border-white/5 rounded-full pointer-events-none" />
            <div className="aspect-square bg-gray-900 rounded-[3rem] rotate-3 overflow-hidden relative group">
              <img 
                src={processImageUrl}
                alt="Production Process" 
                loading="lazy"
                width="800"
                height="800"
                className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" 
              />
              <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-500" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Process;